import { useSearchParams } from "react-router-dom";
import { FilterValue } from "../../../../types/dashboard.types";

export const useQueryParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const setParam = (key: string, value: string | string[] | number[]) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.delete(key);
            if (Array.isArray(value)) {
                value.forEach(v => params.append(key, String(v)));
            } else {
                params.set(key, String(value));
            }

            return params;
        }, { replace: true });
    };
    const setMultipleParams = (
        updates: Record<string, string | string[] | number[] | null>
    ) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);

            Object.entries(updates).forEach(([key, value]) => {
                params.delete(key);

                if (value === null) return;

                if (Array.isArray(value)) {
                    value.forEach(v => params.append(key, String(v)));
                } else {
                    params.set(key, String(value));
                }
            });

            return params;
        }, { replace: true });
    };



    const setArrayParam = (key: string, values: string[] | number[]) => {
        if (!key || !Array.isArray(values)) return;

        const current = new URLSearchParams(searchParams);

        // ✅ Remove existing values for this key
        current.delete(key);

        // ✅ Append ALL values as array params
        values.forEach((v) => {
            current.append(key, String(v));
        });

        setSearchParams(current, { replace: true });
    };


    const getString = (params: URLSearchParams, key: string, fallback = "") =>
        params.get(key) ?? fallback;

    const getArray = (params: URLSearchParams, key: string) =>
        params.getAll(key).filter(Boolean);

    const removeParam = (keys: string[]) => {
        setSearchParams((prevParams) => {
            const params = new URLSearchParams(prevParams);
            keys.forEach((key) => params.delete(key));
            return params;
        }, { replace: true });
    };

    const removeAllParams = () => {
        setSearchParams({}, { replace: true });
    };

    const updateFilters = (updatedFilters: FilterValue) => {
        const params = new URLSearchParams(searchParams);
        Object.entries(updatedFilters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    params.delete(key);
                } else {
                    params.delete(key);
                    value.forEach((v) => params.append(key, String(v)));
                }
                return;
            }
            if (value === "" || value === "All Time" || value === "All Projects") {
                params.delete(key);
                return;
            }
            params.set(key, String(value));
        });
        setSearchParams(params);
    };

    const createUrl = (flag: string, startDate?: string, endDate?: string) => {
        const params = new URLSearchParams(searchParams);
        params.set("flag", flag);
        if (startDate) {
            params.set("startDate", startDate);
        }
        if (endDate) {
            params.set("endDate", endDate);
        }

        return params.toString()
    };

    const hydrateFiltersFromUrl = () => {
        return {
            time: getString(searchParams, "time", "YTD"),
            projectType: getString(searchParams, "projectType", "All Projects"),
            trades: getString(searchParams, "trades", ""),
            progressionTime: getString(searchParams, "progressionTime", "YTD"),
            departmentId: getString(searchParams, "departmentId", ""),


            // arrays
            year: getArray(searchParams, "year").map(Number), // number[]
            month: getArray(searchParams, "month"),           // string[]
            progressionMonths: getArray(searchParams, "progressionMonths"),
            progressionYears: getArray(searchParams, "progressionYears").map(Number),
            projectManager: getArray(searchParams, "projectManager"), // string[]
            projectWorker: getArray(searchParams, "projectWorker"),   // string[]
        };
    };

    return { setParam, getArray, removeParam, searchParams, createUrl, hydrateFiltersFromUrl, setArrayParam, updateFilters, removeAllParams, getString, setMultipleParams };
};

